import { getValuesFromMultilineValue } from "@entrilia/shared/utilities/stringHelper";
import {
  combineValidators,
  definedValidator,
  regexValidator,
  requiredValidator,
  websiteUrlValidator,
} from "@entrilia/shared/utilities/validators";
import type { CreateFundAdminClientRequest, SherWebOrganizationInfo } from "../../../api/clientsManagementTypes";
import type { FormValidationResults } from "../../../utilities/forms";

export interface FundAdminProvisioningForm {
  title: string;
  clientCode: string;
  location: ClientLocation | undefined;
  organizationDomainsMemo: string;
  sherwebOrganization: SherWebOrganizationInfo | null;
  primaryBcTenantId: string;
  primaryBcEnvironment: string;
  websiteUrl: string;
  continueIfClientAlreadyExists: boolean;
  validationResults: FormValidationResults<ValidationKey>;
}

export type ClientLocation = "US" | "EU";

type ValidationKey =
  | "title"
  | "clientCode"
  | "location"
  | "organizationDomainsMemo"
  | "sherwebOrganization"
  | "primaryBcTenantId"
  | "websiteUrl";

export const getInitialFundAdminProvisioningForm = (): FundAdminProvisioningForm => ({
  title: "",
  clientCode: "",
  location: undefined,
  organizationDomainsMemo: "",
  sherwebOrganization: null,
  primaryBcTenantId: "",
  primaryBcEnvironment: "Production",
  websiteUrl: "",
  continueIfClientAlreadyExists: false,
  validationResults: {},
});

const clientCodeValidator = combineValidators(
  requiredValidator,
  regexValidator(/^[a-z0-9]+$/, "Only lowercase letters and numbers are allowed")
);

export const validateFundAdminProvisioningForm = (
  form: FundAdminProvisioningForm
): FormValidationResults<ValidationKey> => {
  return {
    title: requiredValidator(form.title),
    clientCode: clientCodeValidator(form.clientCode),
    location: definedValidator("Required")(form.location),
    organizationDomainsMemo: requiredValidator(form.organizationDomainsMemo),
    sherwebOrganization: definedValidator("Required")(form.sherwebOrganization),
    websiteUrl: websiteUrlValidator()(form.websiteUrl),
    primaryBcTenantId: requiredValidator(form.primaryBcTenantId),
  };
};

export const mapFundAdminProvisioningFormToRequest = (
  form: FundAdminProvisioningForm
): CreateFundAdminClientRequest => ({
  continueIfClientAlreadyExists: form.continueIfClientAlreadyExists,
  title: form.title.trim(),
  clientCode: form.clientCode.trim(),
  usLocation: form.location === "US",
  organizationDomains: getValuesFromMultilineValue(form.organizationDomainsMemo),
  sherwebCustomerId: form.sherwebOrganization?.id ?? "",
  websiteUrl: form.websiteUrl.trim() || undefined,
  primaryBcTenantId: form.primaryBcTenantId,
  primaryBcEnvironment: form.primaryBcEnvironment.trim() || "Production",
});
